import { ROUTES } from '@/constants/routes';
import { usePermissions } from '@/contexts/PermissionsContext';
import type { NavItem, NavSubItem } from '@/config/layoutConfig';
import { prefetchByPath } from '@/utils/routePrefetch';
import { ChevronDownIcon, CogIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export interface AppNavbarProps {
  items: NavItem[];
  openDropdown: string | null;
  onDropdownToggle: (id: string) => void;
  onSubItemClick: (action: () => void) => void;
}

/**
 * Barre de navigation horizontale avec menus déroulants.
 */
export function AppNavbar({
  items,
  openDropdown,
  onDropdownToggle,
  onSubItemClick,
}: AppNavbarProps) {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = usePermissions();

  return (
    <nav
      className="layout-navbar shadow-md flex-shrink-0 relative overflow-visible"
      onClick={e => e.stopPropagation()}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center overflow-visible">
          <div className="flex-1 flex justify-center">
            <div className="flex space-x-0 relative overflow-x-auto sm:overflow-visible">
              {items
                .map(item => {
                  if (item.type === 'dropdown') {
                    const visibleSubItems = item.subItems.filter(sub =>
                      hasPermission(sub.permissionKey ?? item.permissionKey)
                    );
                    return { item, visibleSubItems };
                  }
                  return { item, visibleSubItems: [] as NavSubItem[] };
                })
                .filter(({ item, visibleSubItems }) => {
                  if (item.type === 'dropdown') return visibleSubItems.length > 0;
                  return hasPermission(item.permissionKey);
                })
                .map(({ item, visibleSubItems }) => {
                  const Icon = item.icon;
                  const isDropdownOpen = item.type === 'dropdown' && openDropdown === item.id;

                  if (item.type === 'dropdown') {
                    return (
                      <div key={item.name} className="relative">
                        <button
                          onMouseEnter={() =>
                            visibleSubItems.forEach(
                              s => s.prefetchPath && prefetchByPath(s.prefetchPath)
                            )
                          }
                          onClick={e => {
                            e.stopPropagation();
                            onDropdownToggle(item.id);
                          }}
                          className={`layout-navbar-item flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 text-xs sm:text-sm font-normal whitespace-nowrap transition-all duration-200 rounded-token-md border-r last:border-r-0 cursor-pointer ${
                            isDropdownOpen ? 'layout-navbar-item-active' : ''
                          }`}
                        >
                          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="hidden sm:inline">{item.name}</span>
                          <span className="sm:hidden">{item.name.slice(0, 4)}</span>
                          <ChevronDownIcon
                            className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                          />
                        </button>

                        {isDropdownOpen && (
                          <div className="layout-navbar-dropdown absolute top-full left-0 shadow-xl border rounded-b-md min-w-48 z-[60] mt-0">
                            {visibleSubItems.map((subItem, subIdx) => {
                              if (subItem.isSeparator) {
                                return (
                                  <div
                                    key={`sep-${subIdx}`}
                                    className="border-t border-default my-1"
                                    aria-hidden="true"
                                  />
                                );
                              }
                              const SubIcon = subItem.icon;
                              return (
                                <button
                                  key={subItem.name}
                                  onMouseEnter={() =>
                                    subItem.prefetchPath && prefetchByPath(subItem.prefetchPath)
                                  }
                                  onClick={e => {
                                    e.stopPropagation();
                                    onSubItemClick(subItem.action);
                                  }}
                                  className="layout-navbar-dropdown-item flex items-center space-x-3 w-full text-left px-4 py-3 text-sm transition-colors duration-200 border-b border-inherit last:border-b-0 first:rounded-t-none last:rounded-b-md"
                                >
                                  <SubIcon className="h-4 w-4 flex-shrink-0" />
                                  <span className="whitespace-nowrap">{subItem.name}</span>
                                  {subItem.badge != null && subItem.badge > 0 && (
                                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                                      {subItem.badge > 99 ? '99+' : subItem.badge}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={item.name}
                      onMouseEnter={() =>
                        item.type === 'simple' &&
                        item.prefetchPath &&
                        prefetchByPath(item.prefetchPath)
                      }
                      onClick={item.action}
                      className="layout-navbar-item flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 text-xs sm:text-sm font-normal whitespace-nowrap transition-all duration-200 rounded-token-md border-r last:border-r-0 cursor-pointer"
                    >
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span className="hidden sm:inline">{item.name}</span>
                      <span className="sm:hidden">{item.name.slice(0, 4)}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          {isSuperAdmin && (
            <div className="relative">
              <button
                onClick={e => {
                  e.stopPropagation();
                  navigate(ROUTES.SETTINGS.ROOT);
                }}
                className="layout-navbar-item flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 text-xs sm:text-sm font-normal whitespace-nowrap rounded-token-md cursor-pointer"
              >
                <CogIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Paramètres</span>
                <span className="sm:hidden">Param</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
